import { useEffect, useState, useRef } from "react";
import { useParams, useSearchParams, Link } from "react-router";
import { motion, AnimatePresence } from "framer-motion";
import {
  ArrowLeft,
  Play,
  Pause,
  Copy,
  Link as LinkIcon,
  Share2,
  QrCode,
  Gauge,
} from "lucide-react";
import type { SoundResponse } from "@wizard-sounds/shared";
import { useAudio } from "@/components/audio-provider";
import { api } from "@/lib/api";
import { cn, formatDuration, formatPlayCount } from "@/lib/utils";
import { shareLink } from "@/lib/share";
import { QrModal } from "@/components/qr-modal";
import { SpeedControl } from "@/components/speed-control";
import WaveSurfer from "wavesurfer.js";

export function SoundPermalink() {
  const { slug } = useParams<{ slug: string }>();
  const [searchParams] = useSearchParams();
  const [sound, setSound] = useState<SoundResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { play, isPlaying, stopAll, nowPlayingProgress, nowPlaying, playbackRate } = useAudio();
  const waveformRef = useRef<HTMLDivElement>(null);
  const wavesurferRef = useRef<WaveSurfer | null>(null);
  const [copied, setCopied] = useState<string | null>(null);
  const [showQr, setShowQr] = useState(false);
  const [showSpeed, setShowSpeed] = useState(false);

  useEffect(() => {
    if (!slug) return;
    setLoading(true);
    api.sounds
      .get(slug)
      .then((data) => {
        setSound(data);
        if (searchParams.get("autoplay") === "1") {
          play(data.id, data.audio_url, {
            title: data.title,
            durationMs: data.duration_ms,
            slug: data.slug,
          });
        }
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [slug]);

  // Initialize WaveSurfer with pre-generated peaks (no audio decoding needed)
  useEffect(() => {
    if (!sound || !waveformRef.current) return;

    const ws = WaveSurfer.create({
      container: waveformRef.current,
      waveColor: "rgba(139, 149, 168, 0.4)",
      progressColor: "#ffb649",
      cursorColor: "#ffb649",
      cursorWidth: 0,
      barWidth: 3,
      barGap: 2,
      barRadius: 3,
      height: 80,
      interact: false,
      // Don't load audio — we use the global audio system for playback
      // and sync the visual progress manually below
    });

    // Load pre-generated waveform peaks if available, otherwise fall back to audio URL
    if (sound.waveform_url) {
      fetch(sound.waveform_url)
        .then((r) => r.json())
        .then((peaks: number[]) => ws.load("", [peaks], sound.duration_ms / 1000))
        .catch(() => ws.load(sound.audio_url));
    } else {
      ws.load(sound.audio_url);
    }

    wavesurferRef.current = ws;

    return () => ws.destroy();
  }, [sound]);

  // Sync WaveSurfer progress bar with global audio playback
  useEffect(() => {
    const ws = wavesurferRef.current;
    if (!ws || !sound) return;

    const thisPlaying = nowPlaying?.id === sound.id;
    if (thisPlaying && nowPlayingProgress > 0) {
      ws.seekTo(Math.min(nowPlayingProgress, 1));
    } else if (!thisPlaying) {
      ws.seekTo(0);
    }
  }, [nowPlayingProgress, nowPlaying, sound]);

  const handleCopy = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    setCopied(label);
    setTimeout(() => setCopied(null), 2000);
  };

  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="h-8 w-8 animate-spin rounded-full border-2 border-spark border-t-transparent" />
      </div>
    );
  }

  if (error || !sound) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center">
        <p className="font-display text-lg text-fog tracking-display">{error || "Sound not found"}</p>
        <Link
          to="/"
          className="mt-4 text-sm text-spark hover:text-spark-soft"
        >
          Back to soundboard
        </Link>
      </div>
    );
  }

  const playing = isPlaying(sound.id);
  const permalink = `${window.location.origin}/s/${sound.slug}`;
  const audioUrl = `${window.location.origin}/clip/${sound.slug}.${sound.mime_type?.includes("wav") ? "wav" : "mp3"}`;
  const triggerUrl = `${window.location.origin}/trigger/${sound.slug}`;

  return (
    <div className="min-h-screen">
      <div className="mx-auto max-w-2xl px-4 py-8">
        <Link
          to="/"
          className="mb-6 inline-flex items-center gap-1.5 text-sm text-fog hover:text-parchment"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to soundboard
        </Link>

        <div className="rounded-[18px] border border-mist bg-slate p-6">
          <h1 className="font-display text-2xl font-bold tracking-display">{sound.title}</h1>

          {sound.description && (
            <p className="mt-2 text-fog">{sound.description}</p>
          )}

          <div className="mt-4 flex items-center gap-4 font-mono text-[11px] text-fog tracking-wider">
            <span>{formatDuration(sound.duration_ms)}</span>
            <span>{formatPlayCount(sound.play_count)} plays</span>
          </div>

          <div className="mt-6" ref={waveformRef} />

          <div className="mt-6 flex items-center gap-3">
            <button
              onClick={() => {
                if (playing) {
                  stopAll();
                } else {
                  play(sound.id, sound.audio_url, {
                    title: sound.title,
                    durationMs: sound.duration_ms,
                    slug: sound.slug,
                  });
                }
              }}
              className="flex h-12 w-12 items-center justify-center rounded-full bg-spark transition-colors duration-150 hover:bg-spark-soft"
            >
              {playing ? (
                <Pause className="h-5 w-5 text-ink" />
              ) : (
                <Play className="h-5 w-5 text-ink ml-0.5" />
              )}
            </button>

            <button
              onClick={() => setShowSpeed((v) => !v)}
              aria-expanded={showSpeed}
              className={cn(
                "flex items-center gap-1.5 rounded-full border px-3 py-2 text-sm font-medium transition-colors",
                playbackRate !== 1 || showSpeed
                  ? "border-spark/40 bg-spark/10 text-spark"
                  : "border-mist text-fog hover:border-spark/40 hover:text-parchment",
              )}
            >
              <Gauge className="h-4 w-4" />
              {playbackRate !== 1 ? `${playbackRate}×` : "Speed"}
            </button>
          </div>

          <AnimatePresence initial={false}>
            {showSpeed && (
              <motion.div
                initial={{ opacity: 0, height: 0 }}
                animate={{ opacity: 1, height: "auto" }}
                exit={{ opacity: 0, height: 0 }}
                transition={{ duration: 0.2, ease: [0.22, 1, 0.36, 1] }}
                className="overflow-hidden"
              >
                <SpeedControl className="mt-4 rounded-xl border border-mist bg-slate-raised/40 p-3" />
              </motion.div>
            )}
          </AnimatePresence>

          {sound.tags.length > 0 && (
            <div className="mt-6 flex flex-wrap gap-2">
              {sound.tags.map((tag) => (
                <Link
                  key={tag.id}
                  to={`/?tags=${tag.slug}`}
                  className="rounded-full bg-slate-raised px-2 py-1 font-mono text-[10px] text-fog tracking-wider hover:text-parchment"
                >
                  {tag.name}
                </Link>
              ))}
            </div>
          )}

          {sound.transcript && (
            <div className="mt-6">
              <h3 className="font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
                Transcript
              </h3>
              <p className="mt-1 rounded-xl bg-slate-raised p-3 text-sm italic text-parchment">
                "{sound.transcript}"
              </p>
            </div>
          )}

          <div className="mt-6 border-t border-mist pt-4">
            <h3 className="font-mono text-[10px] font-medium uppercase tracking-wider text-fog">
              Share
            </h3>
            <div className="mt-2 flex gap-2">
              <button
                onClick={() => shareLink(permalink, `${sound.title} · Wizard Sounds`)}
                className="flex flex-1 items-center justify-center gap-2 rounded-xl bg-spark px-4 py-2.5 text-sm font-semibold text-ink transition-transform active:scale-95"
              >
                <Share2 className="h-4 w-4" /> Share
              </button>
              <button
                onClick={() => setShowQr(true)}
                className="flex items-center justify-center gap-2 rounded-xl border border-mist bg-slate px-4 py-2.5 text-sm text-parchment transition-colors hover:border-spark"
              >
                <QrCode className="h-4 w-4" /> QR
              </button>
            </div>
            <div className="mt-2 flex flex-col gap-2">
              <ShareButton
                icon={<LinkIcon className="h-4 w-4" />}
                label="Permalink"
                value={permalink}
                copied={copied === "permalink"}
                onCopy={() => handleCopy(permalink, "permalink")}
              />
              <ShareButton
                icon={<Copy className="h-4 w-4" />}
                label="Audio URL"
                value={audioUrl}
                copied={copied === "audio"}
                onCopy={() => handleCopy(audioUrl, "audio")}
              />
              <ShareButton
                icon={<Share2 className="h-4 w-4" />}
                label="Trigger URL"
                value={triggerUrl}
                copied={copied === "trigger"}
                onCopy={() => handleCopy(triggerUrl, "trigger")}
              />
            </div>
          </div>
        </div>
      </div>
      {showQr && (
        <QrModal url={permalink} title={sound.title} onClose={() => setShowQr(false)} />
      )}
    </div>
  );
}

function ShareButton({
  icon,
  label,
  value,
  copied,
  onCopy,
}: {
  icon: React.ReactNode;
  label: string;
  value: string;
  copied: boolean;
  onCopy: () => void;
}) {
  return (
    <button
      onClick={onCopy}
      className="flex items-center gap-2 rounded-xl border border-mist bg-slate-raised px-3 py-2 text-sm text-parchment hover:border-spark"
    >
      <span className="text-fog">{icon}</span>
      <span className="font-medium">{label}</span>
      <span className="ml-auto truncate font-mono text-[11px] text-fog">
        {copied ? "Copied!" : value}
      </span>
    </button>
  );
}
